import { useContext } from 'react'
import { useMutation, useQuery, UseQueryResult } from 'react-query'
import { useLocation } from 'react-router-dom'
import { HttpVerb } from '../../types/api-types'
import { WorkflowStateInterface } from '../components/wells/well-page'
import { WellState } from '../context/well-context'
import fetchApi, { getLookupData, getWellHeaderData } from '../services/api'
import { FEATURE_FLAGS, isFeatureEnabled } from '../services/feature-flags'
import { UserContext } from '../state/user-state'
import { NewFile } from '@msaf/core-react'

export function useWellHeader(wellId: string) {
  const { state } = useContext(UserContext)
  return useQuery(['wellHeader', wellId], () => getWellHeaderData(wellId, state.token, state.auth_method), {
    refetchOnWindowFocus: false,
    enabled: !!wellId,
  })
}

export function useLookup(fieldId: string, fieldFilters?: Record<string, string | number | boolean | null>) {
  const { state } = useContext(UserContext)
  return useQuery(
    ['lookupData', fieldId, fieldFilters],
    () => getLookupData(fieldId, state.token, state.auth_method, fieldFilters),
    {
      refetchOnWindowFocus: false,
      // Wait for 200ms before marking the data as stale.
      // This helps ensure that the lookup fields fetch the lookup data only once when the form state is first loaded in edit mode.
      staleTime: 200,
      enabled: !!fieldId,
    },
  )
}

export function useLookupDisplayValue(
  fieldId: string,
  value: string,
  isNewlyCreatedOption?: boolean,
  fieldFilters?: Record<string, string | number | boolean | null>,
) {
  const { state } = useContext(UserContext)
  return useQuery(
    ['lookupData', fieldId, value, fieldFilters],
    () => getLookupData(fieldId, state.token, state.auth_method, { id: value, ...fieldFilters }),
    {
      refetchOnWindowFocus: false,
      // Wait for 200ms before marking the data as stale.
      // This helps ensure that the lookup fields fetch the lookup data only once when the form state is first loaded in edit mode.
      staleTime: 200,
      // Fetch display value only when 'value' is present
      // and the selected value is not a newly created option
      enabled: !!value && !isNewlyCreatedOption,
    },
  )
}

export function useGetWellWorkflowState(
  url: string,
  isEnabled: boolean = true,
): UseQueryResult<{ state: WorkflowStateInterface }, Error> {
  const { state } = useContext(UserContext)
  const location = useLocation()
  return useQuery(
    [url, location.pathname],
    () => fetchApi({ url, token: state.token, auth_method: state.auth_method }),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isEnabled,
    },
  )
}

export function useGetWellMetaData(
  url: string,
  isEnabled: boolean = true,
): UseQueryResult<{ state: WellState }, Error> {
  const { state } = useContext(UserContext)
  const location = useLocation()
  return useQuery(
    [url, location.pathname],
    () => fetchApi({ url, token: state.token, auth_method: state.auth_method }),
    {
      retry: false,
      enabled: isEnabled,
    },
  )
}

export function useGetWellData(url: string, isEnabled: boolean = true): UseQueryResult<any, Error> {
  const { state } = useContext(UserContext)
  return useQuery([url], () => fetchApi({ url, token: state.token, auth_method: state.auth_method }), {
    enabled: isEnabled,
  })
}

export function useGetWellLithologyVisualisationData(url: string) {
  const { state } = useContext(UserContext)
  return useQuery([url], () => fetchApi({ url, token: state.token, auth_method: state.auth_method }))
}

export function useGetWellExportReportData(url: string, wellId: string | null | true) {
  const { state } = useContext(UserContext)
  return useQuery([url], () => fetchApi({ url, token: state.token, auth_method: state.auth_method }), {
    // Fetch well data when 'wellId' is present
    enabled: !!wellId,
    cacheTime: 0,
  })
}

export function usePollForExportReportDownload(url: string, wellId: string | null, exportId?: string) {
  const { state } = useContext(UserContext)
  return useQuery([url], () => fetchApi({ url, token: state.token, auth_method: state.auth_method }), {
    refetchInterval: 3000,
    // Poll for completion status when 'wellId' and 'exportId' is present
    enabled: !!wellId && !!exportId,
  })
}

export function useSaveWellData(url: string, method: HttpVerb = HttpVerb.PUT) {
  const { state } = useContext(UserContext)
  return useMutation((fields: string) =>
    fetchApi({ url, method, body: fields, token: state.token, auth_method: state.auth_method }),
  )
}

export function useGetTableData(url: string, ordering: string, page: number = 1) {
  const { state } = useContext(UserContext)
  /**
   * By setting keepPreviousData to true we get a few new things:
   * The data from the last successful fetch available while new data is being requested, even though the query key has changed.
   * When the new data arrives, the previous data is seamlessly swapped to show the new data.
   * isPreviousData is made available to know what data the query is currently providing you
   *
   * More info: https://react-query.tanstack.com/guides/paginated-queries#better-paginated-queries-with-keeppreviousdata
   */
  return useQuery([url, state.token], () => fetchApi({ url, token: state.token, auth_method: state.auth_method }), {
    keepPreviousData: true,
  })
}

export function useDeleteWellData() {
  const { state } = useContext(UserContext)
  return useMutation(async (url: string) =>
    fetchApi({ url, method: HttpVerb.DELETE, token: state.token, auth_method: state.auth_method }),
  )
}

export function useSaveFile(url: string, method: HttpVerb = HttpVerb.POST) {
  const { state } = useContext(UserContext)
  return (file: NewFile) => {
    var fd = new FormData()
    if (file.file) {
      fd.append('file', file.file)
    }
    return fetchApi({
      url,
      method,
      body: fd,
      token: state.token,
      abortSignal: file.abortController?.signal,
      auth_method: state.auth_method,
    })
  }
}

export function useRequestCsvExport({
  url,
  body,
  method = HttpVerb.POST,
  onSuccess,
  onError,
}: {
  url: string
  body: FormData
  method?: HttpVerb
  onSuccess?: (data: any) => void
  onError?: (error: any) => void
}) {
  const { state } = useContext(UserContext)
  return useMutation(async () => fetchApi({ url, method, token: state.token, auth_method: state.auth_method, body }), {
    onSuccess,
    onError,
  })
}

export function useUnauthenticatedApiQuery(url: string) {
  return useQuery([url], () => fetchApi({ url, token: '' }))
}

export function useGetBannerData() {
  const url = '/banner.json'
  return useQuery([url], () => fetchApi({ url, token: '' }), {
    enabled: isFeatureEnabled(FEATURE_FLAGS.ENABLE_BANNER),
  })
}
